<script setup>
import { ref, computed, watch } from 'vue';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';
import Switch from '../../../../../components-next/switch/Switch.vue';

// Props
const props = defineProps({
  initialRedirectConditions: {
    type: String,
    default: ''
  },
  initialHumanAvailabilityMessagingEnabled: {
    type: Boolean,
    default: false
  },
  initialHumanAvailabilityHours: {
    type: Object,
    default: null
  }
});

// Emits
const emit = defineEmits(['update', 'validation']);

// Default hours structure
const getDefaultHours = () => ({
  sunday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
  monday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
  tuesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
  wednesday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
  thursday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
  friday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] },
  saturday: { enabled: false, timeSlots: [{ startTime: '09:00', endTime: '18:00' }] }
});

// State
const redirectConditions = ref(props.initialRedirectConditions || '');
const humanAvailabilityMessagingEnabled = ref(props.initialHumanAvailabilityMessagingEnabled);
const humanAvailabilityHours = ref(
  props.initialHumanAvailabilityHours
    ? JSON.parse(JSON.stringify(props.initialHumanAvailabilityHours))
    : getDefaultHours()
);
const humanAvailabilityHoursErrors = ref({});

// Days of week configuration - mesma ordem do profissional
const daysOfWeek = [
  { key: 'sunday', label: 'Domingo' },
  { key: 'monday', label: 'Segunda-Feira' },
  { key: 'tuesday', label: 'Terça-Feira' },
  { key: 'wednesday', label: 'Quarta-Feira' },
  { key: 'thursday', label: 'Quinta-Feira' },
  { key: 'friday', label: 'Sexta-Feira' },
  { key: 'saturday', label: 'Sábado' }
];

// Computed
const isValid = computed(() => {
  // Se os horários personalizados não estão habilitados, sempre válido
  if (!humanAvailabilityMessagingEnabled.value) {
    return true;
  }

  // Validar todos os horários habilitados
  for (const day of daysOfWeek) {
    const dayKey = day.key;
    if (humanAvailabilityHours.value[dayKey].enabled) {
      const timeSlots = humanAvailabilityHours.value[dayKey].timeSlots;

      // Deve ter pelo menos um slot
      if (!timeSlots || timeSlots.length === 0) {
        return false;
      }

      // Validar cada slot
      for (let i = 0; i < timeSlots.length; i++) {
        if (!validateTimeSlot(dayKey, i)) {
          return false;
        }
      }
    }
  }

  return true;
});

// Limitar entrada a 3000 caracteres
const handleInput = (event) => {
  const value = event.target.value;
  if (value.length <= 3000) {
    redirectConditions.value = value;
  } else {
    // Cortar no limite de 3000 caracteres
    redirectConditions.value = value.substring(0, 3000);
    // Atualizar o valor do textarea para refletir o corte
    event.target.value = redirectConditions.value;
  }
};

// Helper functions for human availability hours
const ensureTimeSlots = (dayKey) => {
  if (humanAvailabilityHours.value[dayKey].enabled &&
      (!humanAvailabilityHours.value[dayKey].timeSlots ||
       humanAvailabilityHours.value[dayKey].timeSlots.length === 0)) {
    humanAvailabilityHours.value[dayKey].timeSlots = [
      { startTime: '09:00', endTime: '17:00' }
    ];
  }
};

const getNextHour = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  let nextHour = hours + 1;

  if (nextHour > 23) {
    nextHour = 0;
  }

  return `${nextHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const addTimeSlot = (dayKey) => {
  const lastSlot = humanAvailabilityHours.value[dayKey].timeSlots.slice(-1)[0];

  if (!lastSlot) {
    humanAvailabilityHours.value[dayKey].timeSlots.push({
      startTime: '09:00',
      endTime: '10:00'
    });
    return;
  }

  const [lastEndHour] = lastSlot.endTime.split(':').map(Number);
  if (lastEndHour === 0) {
    return;
  }

  const lastSlotIndex = humanAvailabilityHours.value[dayKey].timeSlots.length - 1;
  if (!validateTimeSlot(dayKey, lastSlotIndex)) {
    return;
  }

  let newStartTime = lastSlot.endTime;
  let newEndTime = getNextHour(newStartTime);

  const [startHour] = newStartTime.split(':').map(Number);
  if (startHour === 23) {
    newEndTime = '00:00';
  }

  humanAvailabilityHours.value[dayKey].timeSlots.push({
    startTime: newStartTime,
    endTime: newEndTime
  });
};

const removeTimeSlot = (day, index) => {
  if (humanAvailabilityHours.value[day].timeSlots.length > 1) {
    humanAvailabilityHours.value[day].timeSlots.splice(index, 1);
  }
};

const duplicateToOtherDays = (sourceDayKey) => {
  const sourceSlots = humanAvailabilityHours.value[sourceDayKey].timeSlots;

  daysOfWeek.forEach(day => {
    if (day.key !== sourceDayKey && humanAvailabilityHours.value[day.key].enabled) {
      humanAvailabilityHours.value[day.key].timeSlots = sourceSlots.map(slot => ({
        startTime: slot.startTime,
        endTime: slot.endTime
      }));
    }
  });
};

const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const validateTimeSlot = (dayKey, slotIndex) => {
  const day = humanAvailabilityHours.value[dayKey];
  const slot = day.timeSlots[slotIndex];
  const slotErrors = {};

  if (!slot.startTime) {
    slotErrors.startTime = 'Horário de início é obrigatório';
  }
  if (!slot.endTime) {
    slotErrors.endTime = 'Horário de fim é obrigatório';
  }

  if (slot.startTime && slot.endTime) {
    const startMinutes = timeToMinutes(slot.startTime);
    const endMinutes = timeToMinutes(slot.endTime);

    if (startMinutes >= endMinutes && endMinutes !== 0) {
      slotErrors.endTime = 'Horário de fim deve ser após o início';
    } else if (startMinutes === endMinutes) {
      slotErrors.endTime = 'Horário de fim deve ser diferente do início';
    }
  }

  // Check for overlapping time slots
  day.timeSlots.forEach((otherSlot, otherIndex) => {
    if (otherIndex < slotIndex && slot.startTime && slot.endTime && otherSlot.startTime && otherSlot.endTime) {
      const slotStart = timeToMinutes(slot.startTime);
      const slotEnd = timeToMinutes(slot.endTime) || 1440;
      const otherStart = timeToMinutes(otherSlot.startTime);
      const otherEnd = timeToMinutes(otherSlot.endTime) || 1440;

      if ((slotStart < otherEnd && slotEnd > otherStart)) {
        slotErrors.overlap = 'Horários não podem se sobrepor';
      }
    }
  });

  if (!humanAvailabilityHoursErrors.value[dayKey]) {
    humanAvailabilityHoursErrors.value[dayKey] = {};
  }
  humanAvailabilityHoursErrors.value[dayKey][slotIndex] = slotErrors;

  return Object.keys(slotErrors).length === 0;
};

// Emit update data
const emitUpdate = () => {
  emit('update', {
    redirectConditions: redirectConditions.value,
    humanAvailabilityMessagingEnabled: humanAvailabilityMessagingEnabled.value,
    humanAvailabilityHours: humanAvailabilityMessagingEnabled.value ? humanAvailabilityHours.value : null
  });
};

// Validate all slots when enabling custom schedule
const validateAllSlots = () => {
  if (!humanAvailabilityMessagingEnabled.value) return;

  for (const day of daysOfWeek) {
    const dayKey = day.key;
    if (humanAvailabilityHours.value[dayKey].enabled) {
      const timeSlots = humanAvailabilityHours.value[dayKey].timeSlots;
      if (timeSlots && timeSlots.length > 0) {
        for (let i = 0; i < timeSlots.length; i++) {
          validateTimeSlot(dayKey, i);
        }
      }
    }
  }
};

// Watchers
watch([redirectConditions, humanAvailabilityMessagingEnabled, humanAvailabilityHours], () => {
  emitUpdate();
}, { deep: true });

// Validate when enabling custom schedule
watch(humanAvailabilityMessagingEnabled, (newValue) => {
  if (newValue) {
    // Small delay to let the UI update
    setTimeout(() => {
      validateAllSlots();
    }, 100);
  }
});

// No need for transition logic since we're using CSS animation

watch(isValid, (newValue) => {
  emit('validation', newValue);
}, { immediate: true });

// Watch for prop changes (useful for edit mode)
watch(() => props.initialRedirectConditions, (newValue) => {
  if (newValue !== undefined && newValue !== redirectConditions.value) {
    redirectConditions.value = newValue || '';
  }
});

watch(() => props.initialHumanAvailabilityMessagingEnabled, (newValue) => {
  if (newValue !== undefined && newValue !== humanAvailabilityMessagingEnabled.value) {
    humanAvailabilityMessagingEnabled.value = newValue;
  }
});

watch(() => props.initialHumanAvailabilityHours, (newValue) => {
  if (newValue && JSON.stringify(newValue) !== JSON.stringify(humanAvailabilityHours.value)) {
    humanAvailabilityHours.value = JSON.parse(JSON.stringify(newValue));
  } else if (!newValue) {
    // If null or undefined, reset to default
    humanAvailabilityHours.value = getDefaultHours();
  }
});
</script>

<template>
  <div class="space-y-4">
    <div class="relative">
      <div class="flex items-center justify-between mb-2">
        <label class="block text-sm font-medium text-n-slate-12">
          Quando redirecionar para humanos
        </label>
        <span v-if="redirectConditions" class="text-xs text-n-slate-10">{{ redirectConditions.length }}/3000</span>
      </div>
      <Textarea
        v-model="redirectConditions"
        @input="handleInput"
        :rows="2"
        :autoHeight="true"
        :resize="true"
        :minHeight="'5rem'"
        :maxHeight="'18rem'"
        :maxlength="3000"
        placeholder="Descreva as situações em que o agente deve transferir a conversa para um atendente humano. Por exemplo: quando o cliente solicitar falar com alguém, quando expressar insatisfação, quando tiver dúvidas complexas sobre garantia, etc."
      />
      <p class="mt-2 text-sm text-n-slate-10">
        Este campo é opcional. Se não preenchido, o agente usará critérios padrão para redirecionamento.
      </p>
    </div>

    <!-- Custom Schedule Toggle -->
    <div class="mt-6 flex flex-col gap-1.5">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-n-slate-12">Personalização de Mensagens por Horário</label>
        <Switch v-model="humanAvailabilityMessagingEnabled" />
      </div>
      <p class="text-n-slate-10 text-sm mt-1">
        <span class="i-lucide-message-circle mr-1"></span>
        Configurar como o agente informa sobre disponibilidade de atendimento humano baseado nos dias e horários
      </p>

      <!-- Conteúdo condicional do horário personalizado -->
      <div v-if="humanAvailabilityMessagingEnabled" class="mt-3 p-4 bg-n-slate-2 dark:bg-n-slate-3 rounded-lg border border-n-slate-4 animate-fade-in">
        <div class="flex items-center justify-between mb-3">
          <label class="text-sm font-medium text-n-slate-12 block">Horários de Disponibilidade Humana</label>
          <span v-if="humanAvailabilityMessagingEnabled && !isValid" class="text-xs text-n-ruby-9 font-medium">
            <i class="i-lucide-alert-circle w-3 h-3 inline mr-1"></i>
            Corrija os erros nos horários para continuar
          </span>
        </div>

        <!-- Days of Week Schedule -->
        <div class="space-y-4">
          <div v-for="day in daysOfWeek" :key="day.key" class="flex items-start gap-4 py-2">
            <!-- Day Toggle -->
            <div class="flex items-start gap-4 py-2">
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="humanAvailabilityHours[day.key].enabled"
                  @change="() => { ensureTimeSlots(day.key); validateAllSlots(); }"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div class="relative w-11 h-6 bg-n-slate-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-brand/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"></div>
              </label>

              <!-- Day Name -->
              <span class="font-medium text-n-slate-12 min-w-[120px]">
                {{ day.label }}
              </span>
            </div>

            <!-- Time Slots for enabled days -->
            <div v-if="humanAvailabilityHours[day.key].enabled" class="flex-1 flex gap-3 items-center">
              <!-- Time Slots Container -->
              <div class="flex flex-col gap-2">
                <div v-for="(slot, slotIndex) in humanAvailabilityHours[day.key].timeSlots" :key="slotIndex">
                  <div class="flex items-center gap-2">
                    <!-- Start Time -->
                    <input
                      v-model="slot.startTime"
                      type="time"
                      @change="validateTimeSlot(day.key, slotIndex)"
                      @blur="validateTimeSlot(day.key, slotIndex)"
                      class="px-3 py-2 border border-n-slate-3 dark:border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent bg-n-background text-n-slate-12 hover:bg-n-slate-1"
                      :class="{ 'border-n-ruby-6': humanAvailabilityHoursErrors[day.key]?.[slotIndex]?.startTime }"
                    />

                    <!-- Separator -->
                    <span class="text-n-slate-11 font-medium">-</span>

                    <!-- End Time -->
                    <input
                      v-model="slot.endTime"
                      type="time"
                      @change="validateTimeSlot(day.key, slotIndex)"
                      @blur="validateTimeSlot(day.key, slotIndex)"
                      class="px-3 py-2 border border-n-slate-3 dark:border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent bg-n-background text-n-slate-12 hover:bg-n-slate-1"
                      :class="{ 'border-n-ruby-6': humanAvailabilityHoursErrors[day.key]?.[slotIndex]?.endTime }"
                    />

                    <!-- Action Buttons (only on first slot) -->
                    <div v-if="slotIndex === 0" class="flex items-start gap-2">
                      <!-- Add Time Slot Button -->
                      <button
                        type="button"
                        @click="addTimeSlot(day.key)"
                        class="p-1.5 text-n-slate-11 hover:text-n-brand hover:bg-n-slate-1 dark:hover:bg-n-slate-12/10 rounded transition-colors"
                        title="Adicionar Horário"
                      >
                        <span class="i-lucide-plus w-4 h-4"></span>
                      </button>

                      <!-- Duplicate to Other Days Button -->
                      <button
                        type="button"
                        @click="duplicateToOtherDays(day.key)"
                        class="p-1.5 text-n-slate-11 hover:text-n-brand hover:bg-n-slate-1 dark:hover:bg-n-slate-12/10 rounded transition-colors"
                        title="Duplicar para outros dias"
                      >
                        <span class="i-lucide-copy w-4 h-4"></span>
                      </button>
                    </div>

                    <!-- Remove Time Slot Button (only for additional slots) -->
                    <button
                      v-if="slotIndex > 0"
                      type="button"
                      @click="removeTimeSlot(day.key, slotIndex)"
                      class="p-1.5 text-n-ruby-9 hover:text-n-ruby-10 hover:bg-n-ruby-1 dark:hover:bg-n-ruby-12/10 rounded transition-colors"
                      title="Remover"
                    >
                      <span class="i-lucide-trash-2 w-4 h-4"></span>
                    </button>
                  </div>

                  <!-- Error Messages for this slot -->
                  <div v-if="humanAvailabilityHoursErrors[day.key]?.[slotIndex]" class="pl-2 mt-1">
                    <p v-if="humanAvailabilityHoursErrors[day.key][slotIndex].startTime" class="text-xs text-n-ruby-9">
                      {{ humanAvailabilityHoursErrors[day.key][slotIndex].startTime }}
                    </p>
                    <p v-if="humanAvailabilityHoursErrors[day.key][slotIndex].endTime" class="text-xs text-n-ruby-9">
                      {{ humanAvailabilityHoursErrors[day.key][slotIndex].endTime }}
                    </p>
                    <p v-if="humanAvailabilityHoursErrors[day.key][slotIndex].overlap" class="text-xs text-n-ruby-9">
                      {{ humanAvailabilityHoursErrors[day.key][slotIndex].overlap }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Disabled Day Message -->
            <span v-else class="text-n-slate-9 text-sm italic pt-2">
              Dia desabilitado
            </span>
          </div>
        </div>

        <div class="text-xs text-n-slate-10 mt-3">
          <span class="i-lucide-info w-3 h-3 inline mr-1"></span>
          O agente usará esses horários para informar quando um humano poderá entrar em contato. Por exemplo: "Na segunda-feira um atendente entrará em contato".
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Fade in animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}
</style>